import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import useAuth from "@/hooks/useAuth";
import { toast } from "react-hot-toast";
import { restaurant_id as fallbackRestaurantId } from "@/api/lib/url";
import {
  getRewardPointSettingsApi,
  updateRewardPointSettingsApi,
} from "@/api/settings/reward-point-settings";

function PointsRewardSettings() {
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState(true);
  const [earningAmount, setEarningAmount] = useState("1.00");
  const [earningPoints, setEarningPoints] = useState("1");
  const [redeemAmount, setRedeemAmount] = useState("0.50");
  const [redeemPoints, setRedeemPoints] = useState("1");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const restaurantId =
    user?.restaurant_id || localStorage.getItem("restaurant_id") || fallbackRestaurantId;

  const applyRewardSettings = (rawSettings) => {
    if (!rawSettings || typeof rawSettings !== "object") {
      return;
    }

    const settings =
      rawSettings?.reward_point_setting ||
      rawSettings?.reward_points_setting ||
      rawSettings;

    if (settings?.is_reward_points_enabled !== undefined) {
      setIsEnabled(Boolean(settings.is_reward_points_enabled));
    }
    if (settings?.earning_amount !== undefined && settings?.earning_amount !== null) {
      setEarningAmount(String(settings.earning_amount));
    }
    if (settings?.earning_points !== undefined && settings?.earning_points !== null) {
      setEarningPoints(String(settings.earning_points));
    }
    if (settings?.redeeming_amount !== undefined && settings?.redeeming_amount !== null) {
      setRedeemAmount(String(settings.redeeming_amount));
    }
    if (settings?.redeeming_points !== undefined && settings?.redeeming_points !== null) {
      setRedeemPoints(String(settings.redeeming_points));
    }
  };

  useEffect(() => {
    const localSettings = JSON.parse(localStorage.getItem("settings") || "{}");
    const localRewardSettings =
      localSettings?.loyalty?.reward_point_setting || localSettings?.reward_point_setting;
    applyRewardSettings(localRewardSettings);
  }, []);

  useEffect(() => {
    const fetchRewardSettings = async () => {
      if (!restaurantId) {
        return;
      }

      setIsLoading(true);
      const res = await getRewardPointSettingsApi(restaurantId);
      if (res?.status === "success") {
        applyRewardSettings(res?.data || res);
      } else if (res?.message) {
        toast.error(res.message);
      }
      setIsLoading(false);
    };

    fetchRewardSettings();
  }, [restaurantId]);

  const handleSave = async () => {
    if (!restaurantId) {
      toast.error("Restaurant not found.");
      return;
    }

    const payload = {
      is_reward_points_enabled: Boolean(isEnabled),
      earning_amount: Number(earningAmount),
      earning_points: Number(earningPoints),
      redeeming_amount: Number(redeemAmount),
      redeeming_points: Number(redeemPoints),
    };

    if (
      Number.isNaN(payload.earning_amount) ||
      Number.isNaN(payload.earning_points) ||
      Number.isNaN(payload.redeeming_amount) ||
      Number.isNaN(payload.redeeming_points)
    ) {
      toast.error("Please enter valid numeric values.");
      return;
    }

    setIsSaving(true);
    const res = await updateRewardPointSettingsApi(restaurantId, payload);

    if (res?.status === "success") {
      toast.success(res?.message || "Reward point settings updated successfully.");

      const savedSetting =
        res?.data?.reward_point_setting ||
        res?.data?.reward_points_setting ||
        payload;
      const currentSettings = JSON.parse(localStorage.getItem("settings") || "{}");

      localStorage.setItem(
        "settings",
        JSON.stringify({
          ...currentSettings,
          loyalty: {
            ...(currentSettings?.loyalty || {}),
            reward_point_setting: savedSetting,
          },
          reward_point_setting: savedSetting,
        })
      );
    } else {
      toast.error(res?.message || "Failed to update reward point settings.");
    }

    setIsSaving(false);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Points - Reward Settings" }}
      paths={[
        { name: "Home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Settings", path: "/backoffice/settings/take-order-settings" },
        {
          name: "Points - Reward Settings",
          path: "/backoffice/settings/points-reward-settings",
        },
      ]}
    >
      <div className="container mt-5 pb-6">
        <div className="w-full bg-cblack3 p-4 sm:p-5">
          <div className="flex items-center gap-3 border-b border-white/10 pb-4">
            <Switch
              checked={isEnabled}
              onCheckedChange={setIsEnabled}
              className="data-[state=checked]:bg-cgreen"
            />
            <h2 className="text-xl font-semibold text-cwhite">Points - Reward Settings</h2>
          </div>

          <div className="mt-4 space-y-4">
            <section className="rounded-md bg-cblack4 p-4 sm:p-5">
              <h3 className="text-lg font-medium text-cwhite">
                Earning Points (Customer Spends)
              </h3>

              <div className="mt-6 flex flex-wrap items-center gap-3 text-cwhite2">
                <span className="text-base">For every</span>
                <span className="text-xl font-semibold text-cwhite">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={earningAmount}
                  onChange={(e) => setEarningAmount(e.target.value)}
                  className="h-11 w-28 border-white/15 bg-[#3A3B3D] text-center text-2xl font-medium text-cwhite"
                />

                <div className="flex min-w-[160px] flex-col items-center justify-center px-1">
                  <span className="mb-1 text-sm text-cwhite2">Reward</span>
                  <ArrowRight className="text-cwhite" size={24} />
                </div>

                <Input
                  type="number"
                  min="0"
                  value={earningPoints}
                  onChange={(e) => setEarningPoints(e.target.value)}
                  className="h-11 w-28 border-white/15 bg-[#3A3B3D] text-center text-2xl font-medium text-cwhite"
                />
                <span className="text-base">Points</span>
              </div>
            </section>

            <section className="rounded-md bg-cblack4 p-4 sm:p-5">
              <h3 className="text-lg font-medium text-cwhite">
                Redeeming Points (Conversion to payments )
              </h3>

              <div className="mt-6 flex flex-wrap items-center gap-3 text-cwhite2">
                <span className="text-base">For every</span>
                <span className="text-xl font-semibold text-cwhite">£</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(e.target.value)}
                  className="h-11 w-28 border-white/15 bg-[#3A3B3D] text-center text-2xl font-medium text-cwhite"
                />

                <div className="flex min-w-[160px] flex-col items-center justify-center px-1">
                  <span className="mb-1 text-sm text-cwhite2">Converted</span>
                  <ArrowLeft className="text-cwhite" size={24} />
                </div>

                <Input
                  type="number"
                  min="0"
                  value={redeemPoints}
                  onChange={(e) => setRedeemPoints(e.target.value)}
                  className="h-11 w-28 border-white/15 bg-[#3A3B3D] text-center text-2xl font-medium text-cwhite"
                />
                <span className="text-base">Points</span>
              </div>
            </section>

            <Button
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="mt-2 h-11 min-w-[144px] bg-cgreen text-cwhite hover:bg-cgreen/90 disabled:opacity-60"
            >
              {isSaving ? "Saving..." : isLoading ? "Loading..." : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </Layout2>
  );
}

export default PointsRewardSettings;
